import React, { useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  TextField,
  Typography,
} from '@mui/material';

import { AdvertRanking, AdvertsGetResponse, getAdminAdverts, searchAdverts } from '~/services/MerchandiseService';

// Extended advert type that includes admin-only fields returned with adminView=true
type FullAdvert = AdvertsGetResponse['result']['adverts'][number] & {
  isOnline?: boolean;
  isDeleted?: boolean;
  salesCount?: number;
};

const SNACKBAR_OPTIONS = {
  anchorOrigin: { vertical: 'top' as const, horizontal: 'right' as const },
};

export interface UpsertRankingData {
  advertId: string;
  title: string;
  brandName: string | null;
  categoryName: string | null;
  slug: string | null;
  isOnline: boolean | null;
  salesCount: number | null;
  rank: number;
  isNew: boolean;
}

interface Props {
  open: boolean;
  onClose: () => void;
  maxRank: number;
  existingRankings: AdvertRanking[];
  deletedRankings: AdvertRanking[];
  onUpsert: (data: UpsertRankingData) => void;
}

export default function AddRankingDialog({
  open,
  onClose,
  maxRank,
  existingRankings,
  deletedRankings,
  onUpsert,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAdvert, setSelectedAdvert] = useState<FullAdvert | null>(null);
  const [rank, setRank] = useState('');
  const [rankError, setRankError] = useState('');

  // Create lookup map for existing rankings by advertId
  const rankingByAdvertId = useMemo(() => {
    const map = new Map<string, number>();
    existingRankings.forEach((r) => map.set(r.advertId, r.rank));
    return map;
  }, [existingRankings]);

  // Create lookup set for deleted rankings
  const deletedAdvertIds = useMemo(() => {
    return new Set(deletedRankings.map((r) => r.advertId));
  }, [deletedRankings]);

  // Get saved rank for deleted items (for prefilling)
  const deletedRankByAdvertId = useMemo(() => {
    const map = new Map<string, number>();
    deletedRankings.forEach((r) => map.set(r.advertId, r.rank));
    return map;
  }, [deletedRankings]);

  // Step 1: Search returns only IDs, take first 10
  const { data: searchResultIds, isLoading: isSearching } = useQuery({
    queryKey: ['merchandise', 'adverts', 'search', searchQuery],
    queryFn: async ({ signal }) => {
      const response = await searchAdverts(searchQuery, { signal });
      return response.result.adverts.slice(0, 10);
    },
    enabled: searchQuery.length > 0,
  });

  // Step 2: Fetch full advert details for the search results (admin endpoint for isOnline/isDeleted/salesCount)
  const advertIds = searchResultIds?.map((item) => item.id) ?? [];
  const { data: searchResults, isLoading: isFetchingDetails } = useQuery({
    queryKey: ['merchandise', 'admin-adverts', 'bulk', advertIds],
    queryFn: async ({ signal }) => {
      const response = await getAdminAdverts(advertIds, { signal });
      return response.result.adverts;
    },
    enabled: advertIds.length > 0,
  });

  const isLoading = isSearching || isFetchingDetails;

  const handleClose = () => {
    setSearchQuery('');
    setSelectedAdvert(null);
    setRank('');
    setRankError('');
    onClose();
  };

  const validateRank = (value: string): boolean => {
    const numRank = Number(value);
    if (!value || value.trim() === '') {
      setRankError('Rank is required');
      return false;
    }
    if (isNaN(numRank)) {
      setRankError('Rank must be a number');
      return false;
    }
    if (numRank < 1 || numRank > maxRank + 1) {
      setRankError(`Rank must be between 1 and ${maxRank + 1}`);
      return false;
    }
    setRankError('');
    return true;
  };

  const handleSubmit = () => {
    if (!selectedAdvert) {
      enqueueSnackbar('Please select an advert', { variant: 'warning', ...SNACKBAR_OPTIONS });
      return;
    }
    if (!validateRank(rank)) {
      return;
    }

    const isInActive = rankingByAdvertId.has(selectedAdvert.id);
    const isInDeleted = deletedAdvertIds.has(selectedAdvert.id);
    const isNew = !isInActive && !isInDeleted;

    onUpsert({
      advertId: selectedAdvert.id,
      title: selectedAdvert.name,
      brandName: selectedAdvert.brand?.name || null,
      categoryName: selectedAdvert.category?.displayName || null,
      slug: selectedAdvert.slug || null,
      isOnline: selectedAdvert.isOnline ?? null,
      salesCount: selectedAdvert.salesCount ?? null,
      rank: Number(rank),
      isNew,
    });

    const message = isInDeleted
      ? 'Ranking restored successfully'
      : isNew
      ? 'Ranking added successfully'
      : 'Ranking updated successfully';
    enqueueSnackbar(message, { variant: 'success', ...SNACKBAR_OPTIONS });
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Add/Edit Ranking</DialogTitle>
      <DialogContent>
        <Box>
          <TextField
            fullWidth
            label="Search for advert"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setSelectedAdvert(null);
            }}
            placeholder="Search by advert name..."
            helperText="Showing top 10 results. Refine your search for more specific results."
            autoFocus
          />

          <Box mt={2} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            {/* Header row */}
            <Box
              display="flex"
              gap={2}
              px={2}
              py={1}
              sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}
            >
              <Typography variant="caption" fontWeight="bold" sx={{ flex: 3 }}>
                Advert Name
              </Typography>
              <Typography variant="caption" fontWeight="bold" sx={{ width: '120px' }}>
                Brand
              </Typography>
              <Typography variant="caption" fontWeight="bold" sx={{ width: '60px', textAlign: 'right' }}>
                Ranking
              </Typography>
            </Box>
            {/* Content area */}
            <Box sx={{ height: '285px', overflowY: 'auto' }}>
              {isLoading && (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <CircularProgress size={24} />
                </Box>
              )}

              {!isLoading && searchResults && searchResults.length > 0 && (
                <List dense disablePadding>
                  {searchResults.map((advert) => {
                    const existingRank = rankingByAdvertId.get(advert.id);
                    const isDeleted = deletedAdvertIds.has(advert.id);
                    const deletedRank = deletedRankByAdvertId.get(advert.id);
                    return (
                      <ListItem key={advert.id} disablePadding>
                        <ListItemButton
                          selected={selectedAdvert?.id === advert.id}
                          onClick={() => {
                            setSelectedAdvert(advert);
                            // Prefill rank: existing rank → deleted rank → maxRank + 1
                            const prefillRank = existingRank ?? deletedRank ?? maxRank + 1;
                            setRank(String(prefillRank));
                            setRankError('');
                          }}
                        >
                          <Box display="flex" gap={2} width="100%" alignItems="center">
                            <Typography variant="body2" sx={{ flex: 3 }} noWrap>
                              {advert.name}
                              {isDeleted && (
                                <Typography component="span" variant="caption" color="error" sx={{ ml: 1 }}>
                                  (deleted)
                                </Typography>
                              )}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ width: '120px' }} noWrap>
                              {advert.brand?.name || 'No brand'}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ width: '60px', textAlign: 'right' }}
                              color={existingRank !== undefined ? 'primary' : isDeleted ? 'error' : 'text.secondary'}
                            >
                              {existingRank !== undefined ? `#${existingRank}` : isDeleted ? `#${deletedRank}` : 'N/A'}
                            </Typography>
                          </Box>
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              )}

              {!isLoading && searchQuery.length > 0 && searchResults?.length === 0 && (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <Typography color="text.secondary">No adverts found</Typography>
                </Box>
              )}

              {!isLoading && searchQuery.length === 0 && (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <Typography color="text.secondary">Start typing to search for adverts</Typography>
                </Box>
              )}
            </Box>
          </Box>

          {selectedAdvert &&
            (() => {
              const existingRank = rankingByAdvertId.get(selectedAdvert.id);
              const isDeleted = deletedAdvertIds.has(selectedAdvert.id);
              const deletedRank = deletedRankByAdvertId.get(selectedAdvert.id);
              return (
                <Box
                  mt={3}
                  p={2}
                  sx={{ backgroundColor: isDeleted ? 'error.lighter' : 'action.hover', borderRadius: 1 }}
                >
                  <Typography variant="subtitle2" gutterBottom>
                    Selected Advert:
                    {isDeleted && (
                      <Typography component="span" variant="caption" color="error" sx={{ ml: 1 }}>
                        (will be restored)
                      </Typography>
                    )}
                  </Typography>
                  <Box display="flex" gap={2} alignItems="center">
                    <Typography variant="body1" fontWeight="bold" sx={{ flex: 3 }} noWrap>
                      {selectedAdvert.name}
                    </Typography>
                    <Typography variant="body1" fontWeight="bold" color="text.secondary" sx={{ width: '120px' }} noWrap>
                      {selectedAdvert.brand?.name || 'No brand'}
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight="bold"
                      sx={{ width: '60px', textAlign: 'right' }}
                      color={existingRank !== undefined ? 'primary' : isDeleted ? 'error' : 'text.secondary'}
                    >
                      {existingRank !== undefined ? `#${existingRank}` : isDeleted ? `#${deletedRank}` : 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              );
            })()}

          <Box mt={3}>
            <TextField
              fullWidth
              label="Rank"
              value={rank}
              onChange={(e) => {
                const value = e.target.value;
                // Only allow digits
                if (value === '' || /^\d+$/.test(value)) {
                  setRank(value);
                  if (value) validateRank(value);
                }
              }}
              error={!!rankError}
              helperText={
                rankError || `Enter a rank between 1 and ${maxRank + 1} (higher numbers will be placed at the end)`
              }
              placeholder={`1-${maxRank + 1}`}
              disabled={!selectedAdvert}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="text">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary" disabled={!selectedAdvert || !rank}>
          Add/Edit Ranking
        </Button>
      </DialogActions>
    </Dialog>
  );
}
